import { Box, Dialog, InputAdornment, Stack, TextField } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import { isMacOs } from 'react-device-detect';
import styles from './CommandMenu.styles';
import IconMi from 'components/common/icon/IconMi';
import { useCheckUserAccess } from 'hooks/useCheckUserAccess';
import getData, { filterByCondition, MainMenuItemsList } from 'app/navbar/MenuConfig';
import { selectAuthForNavigation, useAppDispatch, useAppSelector } from 'store/hooks';
import { useDebounce } from 'use-debounce';
import IconHandler from 'components/common/icon/IconHandler';
import { customRequestAPI } from 'api/api';
import FormComponent from 'components/common/form/Form';
import { commandMenuAPI } from 'api/command-menu';
import {
    onSaveCommandMenuNewRecentItems,
    getCommandMenuLastUpdatedDataTime,
    getCommandMenuNewRecentItems,
    getCommandMenuRecentItems,
    saveLoadedCommandMenuSavedItems,
} from './CommandMenuStorage';
import { RootState } from 'store/store';
import { createSelector } from '@reduxjs/toolkit';
import { useMiNavigation } from 'hooks/useNavigation';

interface CommandMenuProps {}

interface CommandMenuResultItemProps {
    item: any;
    onMouseOver: (event: React.MouseEvent<HTMLElement>) => void;
    onClick: (event: React.MouseEvent<HTMLElement>) => void;
    currentActionType: string;
    updateActionType: (action: string) => void;
    isActive: boolean;
    index: string;
}

interface CommandMenuResultHeadingProps {
    title: string;
    labelType?: 'custom' | 'navigate_to' | 'create_new';
}

export interface CommandMenuRecentItem {
    item_id: number | string;
    object_type: string;
    name: string;
    order?: number;
}

export function CommandMenuResultHeading(props: CommandMenuResultHeadingProps) {
    const { t } = useBundleTranslation(['components/command-menu/command_menu']);
    const { title, labelType } = props;
    let headingText;

    switch (labelType) {
        case 'custom':
            headingText = title;
            break;
        case 'create_new':
            headingText = `${t('create_new_label')} ${title}`;
            break;
        default:
            headingText = `${t('navigate_to_label')} ${title}`;
            break;
    }
    return <Box sx={styles.headingElement}>{headingText}</Box>;
}
export function CommandMenuResultItem(props: CommandMenuResultItemProps) {
    const { t } = useBundleTranslation(['components/command-menu/command_menu']);
    const {
        item,
        onMouseOver = () => {},
        onClick = () => {},
        isActive,
        index,
        currentActionType,
        updateActionType = () => {},
    } = props;

    let icon;

    switch (item.itemType) {
        case 'custom_link':
        case 'recent_item':
            icon = item.icon;
            break;
        case 'create_new':
        case 'create_new_from':
            icon = { type: 'mi', value: 'new' };
            break;
        default:
            icon = { type: 'mi', value: 'arrow-right' };
            break;
    }

    const labelSuffix = item?.labelSuffix ? item.labelSuffix : 'list';
    const labelPrefix = item?.itemType ? item.itemType : 'go_to';

    const isShowLabelPrefix = ['custom_link', 'recent_item', 'none'].includes(labelPrefix) ? false : true;

    return (
        <Stack
            direction={'row'}
            spacing={1}
            sx={styles.resultElement}
            className={`command-menu-result-item command-menu-result-item--${index} ${isActive ? 'active' : ''}`}
            onClick={onClick}
            onMouseOver={onMouseOver}
        >
            <Box sx={styles.resultElementIcon}>
                {icon && <IconHandler icon={icon} sx={{ fontSize: icon?.type === 'fa' ? 14 : 16 }} />}
            </Box>
            <Box sx={styles.resultElementContent}>
                <Box sx={styles.resultElementLabel}>
                    {isShowLabelPrefix && <>{t(`label_prefix.${labelPrefix}`)} </>}
                    <Box component={'strong'}>{item.label}</Box>
                    {labelSuffix != 'none' && <> {t(`search_item_label_suffix.${labelSuffix}`)}</>}
                </Box>
                {item?.desc && <Box sx={styles.resultElementDesc}>{item.desc}</Box>}
            </Box>
            <Stack
                direction={'row'}
                flexShrink={0}
                spacing={1}
                className={'command-menu-action-holder'}
                sx={styles.resultElementActionHolder}
            >
                {item?.itemType == 'recent_item' ? (
                    <>
                        <Stack
                            className={`clickable ${currentActionType != 'view' ? 'active' : ''}`}
                            direction={'row'}
                            spacing={1}
                            sx={styles.resultElementActionButton}
                            onClick={() => {
                                updateActionType('');
                            }}
                        >
                            <Box sx={styles.resultElementActionButtonIcon}>
                                <IconMi icon="gear" />
                            </Box>
                            <Box>{t('action_button_label.edit')}</Box>
                        </Stack>
                        {item?.viewHref && (
                            <Stack
                                className={`clickable ${currentActionType == 'view' ? 'active' : ''}`}
                                direction={'row'}
                                spacing={1}
                                sx={styles.resultElementActionButton}
                                onClick={() => {
                                    updateActionType('view');
                                }}
                            >
                                <Box sx={styles.resultElementActionButtonIcon}>
                                    <IconMi icon="view" />
                                </Box>
                                <Box>{t('action_button_label.view')}</Box>
                            </Stack>
                        )}
                    </>
                ) : (
                    <>
                        <Stack
                            direction={'row'}
                            spacing={1}
                            sx={styles.resultElementActionButton}
                            className={'action-icon'}
                        >
                            <Box sx={styles.resultElementActionButtonIcon}>
                                <IconMi icon="enter" />
                            </Box>
                        </Stack>
                    </>
                )}
            </Stack>
        </Stack>
    );
}
export default function CommandMenu(props: CommandMenuProps) {
    const { t } = useBundleTranslation([
        'components/command-menu/command_menu',
        'components/common/navbar/navbar_menu',
    ]);
    const checkCondition = useCheckUserAccess();

    const state = useAppSelector(selectAuthForNavigation);
    const navigate = useMiNavigation();
    const dispatch = useAppDispatch();

    const userId = useMemo(() => {
        return state.auth?.userInfo?.hijack_user_id && state.auth?.userInfo?.hijack_user_id > 0
            ? state.auth.userInfo.hijack_user_id
            : (state.auth?.userInfo?.user_id ?? 0);
    }, [state]);

    const [updatedDataTime, setUpdatedDataTime] = useState<number>(0);

    const [searchFieldValue, setSearchFieldValue] = useState<string>('');
    const [searchValue] = useDebounce(searchFieldValue, 300);

    const [newModalData, setNewModalData] = useState<any>();

    const defaultEmptySearchListRef = useRef<any>({
        recent_content: {
            label: t('recent_content_label'),
            items: [],
            labelType: 'custom',
            keywords: {
                manuLabel: true,
            },
        },
        create_new: {
            label: t('create_new_label'),
            items: [],
            labelType: 'custom',
            keywords: {
                manuLabel: true,
            },
        },
    });
    const searchDataRef = useRef<any>({
        create_new: {
            label: t('create_new_label'),
            items: [],
            labelType: 'custom',
            keywords: {
                manuLabel: true,
            },
        },
        create_new_dataset: {
            label: t('new.dataset'),
            items: [],
            labelType: 'create_new',
            keywords: {
                custom: ['create_new_label'],
                entity: ['dataset'],
            },
        },
        create_new_metric: {
            label: t('new.metric'),
            items: [],
            labelType: 'create_new',
            keywords: {
                custom: ['create_new_label'],
                entity: ['metric'],
            },
        },
        create_new_external_report: {
            label: t('new.ext_report'),
            items: [],
            labelType: 'create_new',
            keywords: {
                custom: ['create_new_label'],
                entity: ['external_report'],
            },
        },
        create_new_bulk_external_report: {
            label: t('new.bulk_add_ext_report'),
            items: [],
            labelType: 'create_new',
            keywords: {
                custom: ['create_new_label'],
                manuLabel: true,
            },
        },
        create_new_bulk_external_content: {
            label: t('new.bulk_add_ext_content'),
            items: [],
            labelType: 'create_new',
            keywords: {
                custom: ['create_new_label'],
                manuLabel: true,
            },
        },
        create_new_dimension: {
            label: t('new.dimension'),
            items: [],
            labelType: 'create_new',
            keywords: {
                custom: ['create_new_label'],
                entity: ['dimension'],
                entityAlias: ['segment'],
            },
        },
        navigate_to: {
            label: t('navigate_to_label') + '...',
            items: [],
            labelType: 'custom',
        },
        alerts: {
            label: t('content.alerts'),
            items: [],
            keywords: {
                entity: ['alert'],
            },
        },
        user_and_groups: {
            label: t('admin.user'),
            items: [],
            keywords: {
                manuLabel: true,
            },
        },
        collection_and_storage: {
            label: t('admin.data_source'),
            items: [],
            keywords: {
                entity: ['data_source'],
                manuLabel: true,
            },
        },
        concierge: {
            label: t('admin.concierge'),
            items: [],
            keywords: {
                manuLabel: true,
            },
        },
        distribution: {
            label: t('admin.distribution'),
            items: [],
            keywords: {
                manuLabel: true,
            },
        },
        reference_object: {
            label: t('admin.reference_object'),
            items: [],
            keywords: {
                manuLabel: true,
            },
        },
        plugin: {
            label: t('admin.plugin'),
            items: [],
            keywords: {
                manuLabel: true,
            },
        },
        utilities: {
            label: t('admin.utilities'),
            items: [],
            keywords: {
                manuLabel: true,
            },
        },
        system: {
            label: t('admin.system'),
            items: [],
            keywords: {
                manuLabel: true,
            },
        },
        account: {
            label: t('group_label.user_menu'),
            items: [],
            keywords: {
                manuLabel: true,
            },
            labelType: 'custom',
        },
    });

    const isFocusSearchField = useRef<boolean>(true);

    //is show command menu
    const [isShowCommandMenuPopup, setIsShowCommandMenuPopup] = useState<boolean>(false);
    const isShowCommandMenuPopupRef = useRef<boolean>(false);
    const updateIsShowCommandMenuPopup = useCallback((isShow: boolean) => {
        isShowCommandMenuPopupRef.current = isShow;
        setIsShowCommandMenuPopup(isShow);
    }, []);

    //selected command item index
    const [activeCommandMenuPopupItem, setActiveCommandMenuPopupItem] = useState<{
        groupIndex: number;
        itemIndex: number;
    }>({
        groupIndex: 0,
        itemIndex: 0,
    });
    const activeCommandMenuPopupItemRef = useRef<{ groupIndex: number; itemIndex: number }>({
        groupIndex: 0,
        itemIndex: 0,
    });
    const updateActiveCommandMenuPopupItem = useCallback((groupIndex: number, itemIndex: number) => {
        activeCommandMenuPopupItemRef.current = {
            groupIndex: groupIndex,
            itemIndex: itemIndex,
        };
        setActiveCommandMenuPopupItem({
            groupIndex: groupIndex,
            itemIndex: itemIndex,
        });
    }, []);

    //active sub group key
    const [activeCommandMenuSubGroup, setActiveCommandMenuSubGroup] = useState<string>('');
    const activeCommandMenuSubGroupRef = useRef<string>('');
    const updateActiveCommandMenuSubGroup = useCallback((subGroupKey: string) => {
        activeCommandMenuSubGroupRef.current = subGroupKey;
        setActiveCommandMenuSubGroup(subGroupKey);
    }, []);

    //action type
    const [actionType, setActionType] = useState<string>('');
    const actionTypeRef = useRef<string>('');
    const updateActionType = useCallback((actionValue: string) => {
        actionTypeRef.current = actionValue;
        setActionType(actionValue);
    }, []);

    const searchResultRef = useRef<any[]>([]);
    const searchResult = useMemo(() => {
        let result;
        const searchString = searchValue.trim();
        if (searchString > '') {
            //search result by string
            updateActiveCommandMenuSubGroup('');
            result = Object.values(searchDataRef.current)
                .map((group: any) => {
                    return {
                        ...group,
                        items: group.items.filter((item: any) => {
                            return item.keywords.some((keyword: string) => {
                                return keyword.toLowerCase().includes(searchString.toLowerCase());
                            });
                        }),
                    };
                })
                .filter((group: any) => group.items.length > 0);
        } else {
            if (activeCommandMenuSubGroupRef.current) {
                //show selected sub group of default
                result = [searchDataRef.current[activeCommandMenuSubGroupRef.current]];
            } else {
                //show default create and recent content items
                result = Object.values(defaultEmptySearchListRef.current).filter(
                    (group: any) => group.items.length > 0,
                );
            }
        }

        searchResultRef.current = result;
        updateActiveCommandMenuPopupItem(0, 0);
        return result;
    }, [searchValue, activeCommandMenuSubGroup, updatedDataTime]);

    const scrollToActiveItem = useCallback(() => {
        const activeIndex = `${activeCommandMenuPopupItemRef.current.groupIndex}-${activeCommandMenuPopupItemRef.current.itemIndex}`;
        const container: HTMLElement | null = document.querySelector('.command-menu-result-container');
        const element: HTMLElement | null = document.querySelector(`.command-menu-result-item--${activeIndex}`);
        if (container && element) {
            const groupHeadingHeight = 33;
            if (activeIndex == '0-0') {
                //scroll to top of the container (first element)
                container.scrollTo({
                    top: 0,
                    behavior: 'instant',
                });
            } else if (
                element.getBoundingClientRect().top <
                container.getBoundingClientRect().top + groupHeadingHeight
            ) {
                //scroll up, element under group heading
                container.scrollTo({
                    top:
                        container.scrollTop -
                        (container.getBoundingClientRect().top +
                            groupHeadingHeight -
                            element.getBoundingClientRect().top),
                    behavior: 'instant',
                });
            } else if (element.getBoundingClientRect().bottom > container.getBoundingClientRect().bottom) {
                //scroll down, element over bottom of the container
                element.scrollIntoView({
                    behavior: 'instant',
                    block: 'nearest',
                });
            }
        }
    }, []);

    const loadRecentItems = useCallback((force?: boolean) => {
        const lastUpdatedTime = getCommandMenuLastUpdatedDataTime(userId);
        const now = Date.now();
        const cacheTime = 30;
        const elapsedMinutes = (now - parseInt(lastUpdatedTime, 10)) / (1000 * 60);

        if (force || !lastUpdatedTime || elapsedMinutes > cacheTime) {
            commandMenuAPI.getRecentContent().then((recentItems) => {
                saveLoadedCommandMenuSavedItems(userId, recentItems, now.toString());
            });
        }
    }, []);

    const keyPressHandler = useCallback((event: KeyboardEvent) => {
        switch (event.key) {
            case 'k':
                if ((event.metaKey && isMacOs) || (event.ctrlKey && !isMacOs)) {
                    event.preventDefault();
                    if (!isShowCommandMenuPopupRef.current) {
                        const recentItemsData = getCommandMenuRecentItems(userId);
                        recentItemsData.forEach((recentItem: CommandMenuRecentItem) => {
                            fillRecentItems(recentItem);
                        });
                        setUpdatedDataTime(Date.now());
                    }
                    updateIsShowCommandMenuPopup(!isShowCommandMenuPopupRef.current);
                }
                break;
            case 'Backspace':
                if (isShowCommandMenuPopupRef.current) {
                    if (activeCommandMenuSubGroupRef.current) {
                        updateActiveCommandMenuSubGroup('');
                        event.preventDefault();
                    }
                }
                break;
            case 'Escape':
                if (isShowCommandMenuPopupRef.current) {
                    if (activeCommandMenuSubGroupRef.current) {
                        updateActiveCommandMenuSubGroup('');
                    } else {
                        updateIsShowCommandMenuPopup(false);
                    }
                    event.preventDefault();
                }
                break;
            case 'Enter':
                if (isShowCommandMenuPopupRef.current) {
                    event.preventDefault();
                    if (searchResultRef.current.length) {
                        makeAction();
                    }
                }
                break;
            case 'ArrowLeft':
            case 'ArrowRight':
                if (isShowCommandMenuPopupRef.current) {
                    event.preventDefault();
                    if (searchResultRef.current.length) {
                        const currentItem =
                            searchResultRef.current[activeCommandMenuPopupItemRef.current.groupIndex].items[
                                activeCommandMenuPopupItemRef.current.itemIndex
                            ];

                        if (currentItem.itemType == 'recent_item' && currentItem.viewHref) {
                            const newAction = actionTypeRef.current === 'view' ? '' : 'view';
                            updateActionType(newAction);
                        }
                    }
                }
                break;
            case 'ArrowUp':
                if (isShowCommandMenuPopupRef.current) {
                    event.preventDefault();
                    if (searchResultRef.current.length) {
                        //Not a first element in the group
                        if (activeCommandMenuPopupItemRef.current.itemIndex !== 0) {
                            updateActiveCommandMenuPopupItem(
                                activeCommandMenuPopupItemRef.current.groupIndex,
                                activeCommandMenuPopupItemRef.current.itemIndex - 1,
                            );
                        } else {
                            //The first element in the group
                            if (activeCommandMenuPopupItemRef.current.groupIndex == 0) {
                                const lastGroupIndex = searchResultRef.current.length - 1;
                                updateActiveCommandMenuPopupItem(
                                    lastGroupIndex,
                                    searchResultRef.current[lastGroupIndex].items.length - 1,
                                );
                            } else {
                                const prevGroupIndex = activeCommandMenuPopupItemRef.current.groupIndex - 1;
                                updateActiveCommandMenuPopupItem(
                                    prevGroupIndex,
                                    searchResultRef.current[prevGroupIndex].items.length - 1,
                                );
                            }
                        }
                        scrollToActiveItem();
                    }
                }
                break;
            case 'ArrowDown':
                if (isShowCommandMenuPopupRef.current) {
                    event.preventDefault();
                    if (searchResultRef.current.length) {
                        const currentGroupItems =
                            searchResultRef.current[activeCommandMenuPopupItemRef.current.groupIndex].items;

                        //Not a last element in the group
                        if (activeCommandMenuPopupItemRef.current.itemIndex !== currentGroupItems.length - 1) {
                            updateActiveCommandMenuPopupItem(
                                activeCommandMenuPopupItemRef.current.groupIndex,
                                activeCommandMenuPopupItemRef.current.itemIndex + 1,
                            );
                        } else {
                            //The last element in the group
                            const isLastGroup =
                                activeCommandMenuPopupItemRef.current.groupIndex == searchResultRef.current.length - 1;

                            if (isLastGroup) {
                                updateActiveCommandMenuPopupItem(0, 0);
                            } else {
                                updateActiveCommandMenuPopupItem(
                                    activeCommandMenuPopupItemRef.current.groupIndex + 1,
                                    0,
                                );
                            }
                        }
                        scrollToActiveItem();
                    }
                }
                break;
        }
    }, []);

    const createNewFunc = useCallback((url: string, popupSettings?: any) => {
        setNewModalData('');

        if (popupSettings) {
            setNewModalData({ ...popupSettings, url });
        } else {
            customRequestAPI
                .post(url, {})
                .then((response: any) => {
                    if ('ERROR' === response.data.status) alert(response.data.message);
                    else if (response.data.redirectUrl) setTimeout(() => navigate(response.data.redirectUrl), 100);
                })
                .catch(() => {});
        }
    }, []);

    const makeAction = useCallback(() => {
        const currentItem =
            searchResultRef.current[activeCommandMenuPopupItemRef.current.groupIndex].items[
                activeCommandMenuPopupItemRef.current.itemIndex
            ];

        if (currentItem.subGroupKey) {
            updateActiveCommandMenuSubGroup(currentItem.subGroupKey);
            return false;
        }

        updateIsShowCommandMenuPopup(false);

        if (currentItem.action) {
            currentItem.action({ dispatch, createNewFunc });
        } else if (actionTypeRef.current == 'view' && currentItem.viewHref) {
            navigate(currentItem.viewHref);
        } else if (currentItem.href) {
            if (currentItem.newTab) {
                window.open(currentItem.href, '_blank', 'noopener,noreferrer');
            } else if (currentItem.isExternal) {
                window.location = currentItem.href;
            } else {
                navigate(currentItem.href);
            }
        }
    }, []);

    const processSearchItemKeywords = (keywordsData: any, menuLabel: string, translateLabel: boolean = true) => {
        const result = [];
        if (keywordsData?.entity) {
            keywordsData.entity.forEach((item: any) => {
                result.push(t(`entity_title.${item}`, { count: 1 }).toLowerCase());
                result.push(t(`entity_title.${item}`, { count: 5 }).toLowerCase());
            });
        }
        if (keywordsData?.entityAlias) {
            keywordsData.entityAlias.forEach((item: any) => {
                result.push(t(`entity_alias.${item}`, { count: 1 }).toLowerCase());
                result.push(t(`entity_alias.${item}`, { count: 5 }).toLowerCase());
            });
        }
        if (keywordsData?.custom) {
            keywordsData.custom.forEach((item: any) => {
                result.push(t(item).toLowerCase());
            });
        }
        if (keywordsData?.manuLabel) {
            if (translateLabel == false) {
                result.push(menuLabel.toLowerCase());
            } else {
                result.push(t(menuLabel).toLowerCase());
            }
        }
        return result;
    };
    const fillSearchItems = (menuItem: any, isDefaultCollection?: boolean) => {
        let groupName = 'navigate_to';
        const searchItems = isDefaultCollection ? defaultEmptySearchListRef.current : searchDataRef.current;
        if (menuItem.searchData?.groupKey && searchItems[menuItem.searchData.groupKey]) {
            groupName = menuItem.searchData.groupKey;
        }

        const label = menuItem.searchData?.label ?? menuItem.label;
        searchItems[groupName].items.push({
            label: menuItem?.translate == false ? label : t(label),
            desc: menuItem.searchData?.desc ? t(`search_item_desc.${menuItem.searchData.desc}`) : undefined,
            itemType: menuItem.searchData.itemType,
            labelSuffix: menuItem.searchData.labelSuffix,
            subGroupKey: menuItem.searchData.subGroupKey,
            icon: menuItem.icon,
            href: menuItem.href,
            viewHref: menuItem.viewHref,
            action: menuItem.action,
            isExternal: menuItem.isExternal,
            newTab: menuItem.newTab,
            keywords: [
                ...processSearchItemKeywords(searchItems[groupName]?.keywords, searchItems[groupName]?.label),
                ...processSearchItemKeywords(menuItem.searchData?.keywords, menuItem.label, menuItem?.translate),
            ],
        });
    };

    const saveRecentContentItems = useCallback(() => {
        let newRecentItems = [...getCommandMenuNewRecentItems(userId)].map(
            (item: CommandMenuRecentItem, index: number) => {
                return { ...item, order: index + 1 };
            },
        );

        if (newRecentItems.length > 0) {
            onSaveCommandMenuNewRecentItems(userId);
            const blob = new Blob([JSON.stringify({ data: newRecentItems })], { type: 'application/json' });
            navigator.sendBeacon('/data/command-menu/recent-content', blob);
        }
    }, []);

    useEffect(() => {
        loadRecentItems();
        const menu: MainMenuItemsList = getData(t, state, checkCondition, 'all', true);
        menu.forEach((item) => {
            //Fill default create new items
            if ('key' in item && ['new'].includes(item.key) && item.children) {
                item.children.forEach((child) => {
                    if ('searchData' in child) {
                        fillSearchItems(
                            {
                                ...child,
                                searchData: {
                                    ...child.searchData,
                                    itemType: 'custom_link',
                                    subGroupKey: child?.children?.length ? child?.searchData?.subGroupKey : undefined,
                                },
                            },
                            true,
                        );
                    }
                });
            }

            //Fill search items
            if ('key' in item && ['new', 'editor', 'admin', 'user'].includes(item.key) && item.children) {
                item.children.forEach((child) => {
                    if ('children' in child && child?.children?.length) {
                        child.children.forEach((subChild) => {
                            if ('searchData' in subChild) {
                                fillSearchItems(subChild);
                            }
                        });
                    } else if ('searchData' in child) {
                        fillSearchItems(child);
                    }
                });
            }
        });
        const userAndGroups: any = [
            {
                label: 'admin.users',
                href: '/editor/user',
                condition: [{ type: 'isAdmin' }],
                searchData: {
                    desc: 'user',
                    keywords: {
                        entity: ['user'],
                    },
                    groupKey: 'user_and_groups',
                },
            },
            {
                label: 'admin.group',
                href: '/editor/group',
                condition: [{ type: 'adminOrPrivilege', params: 'PRIV_CREATE_MANAGE_GROUPS' }],
                searchData: {
                    desc: 'group',
                    keywords: {
                        entity: ['group'],
                    },
                    groupKey: 'user_and_groups',
                },
            },
            {
                label: 'admin.business_unit',
                href: '/editor/business-unit',
                condition: [{ type: 'adminOrPrivilege', params: 'PRIV_CREATE_BUSINESS_UNITS' }],
                searchData: {
                    keywords: {
                        entity: ['business_unit'],
                    },
                    groupKey: 'user_and_groups',
                },
            },
            {
                label: 'admin.privilege_set',
                href: '/editor/privilege-set',
                searchData: {
                    keywords: {
                        entity: ['privilege_set'],
                    },
                    groupKey: 'user_and_groups',
                },
            },
            {
                label: 'admin.user_defaults',
                href: '/editor/user/defaults',
                condition: [{ type: 'isAdmin' }],
                searchData: {
                    desc: 'user_defaults',
                    keywords: {
                        manuLabel: true,
                    },
                    groupKey: 'user_and_groups',
                },
            },
            {
                label: 'admin.access_request',
                href: '/editor/user/access-request',
                condition: [{ type: 'havePrivilege', params: 'PRIV_MANAGE_ACCESS_REQUESTS' }],
                searchData: {
                    keywords: {
                        manuLabel: true,
                    },
                },
            },
        ];

        filterByCondition(userAndGroups, checkCondition).forEach((subChild) => {
            if ('searchData' in subChild) {
                fillSearchItems(subChild);
            }
        });

        if (searchDataRef.current.create_new_external_report.items.length) {
            fillSearchItems({
                label: 'new.ext_report',
                href: '/editor/element/extreport/new',
                searchData: {
                    groupKey: 'create_new_external_report',
                    itemType: 'create_new',
                    labelSuffix: 'none',
                },
            });
        }
        if (checkCondition.isAdmin()) {
            fillSearchItems({
                label: 'root.admin',
                href: '/admin',
                searchData: {
                    labelSuffix: 'page',
                    keywords: {
                        manuLabel: true,
                    },
                },
            });
        }

        const handleBeforeUnload = (event: any) => {
            saveRecentContentItems();
        };

        const handlePageVisibilityChange = () => {
            if (document.hidden) {
                saveRecentContentItems();
            }
        };

        document.addEventListener('visibilitychange', handlePageVisibilityChange);
        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('keydown', keyPressHandler);
        return () => {
            document.removeEventListener('visibilitychange', handlePageVisibilityChange);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('keydown', keyPressHandler);
        };
    }, []);

    const fillRecentItems = (recentItem: CommandMenuRecentItem) => {
        const aliasData: any = {
            app: { icon: 'portal-pages', link: '/editor/page/:entityId' },
            announcement: { icon: 'announcements', link: '/editor/announcement/:entityId' },
            document: { icon: 'documents', link: '/editor/document/:entityId' },
            'kpi-alert-rule': { icon: 'alert', link: '/editor/kpi-alert-rule/:entityId' },
            tag: { icon: 'tags', link: '/editor/topic/:entityId' },
            'glossary-term': { icon: 'glossary', link: '/editor/glossary/:entityId' },
            'custom-field': { icon: 'custom-field', link: '/editor/custom-field/:entityId' },
            category: { icon: 'categories', link: '/editor/category/:entityId' },
            folder: { icon: 'folders', link: '/notification/folder/:entityId' },
            burst: { icon: 'burst', link: '/notification/burst/:entityId' },
            dataset: { icon: 'datasets', link: '/editor/dataset/:entityId', viewLink: '/dataset/:entityId' },
            metric: { icon: 'metric', link: '/editor/element/metric/:entityId', viewLink: '/chart/:entityId' },
            report: { icon: 'report', link: '/editor/element/report/:entityId', viewLink: '/report/:entityId' },
            'external-report': {
                icon: 'external-report',
                link: '/editor/element/extreport/:entityId',
                viewLink: '/extreport/:entityId',
            },
            'external-content': {
                icon: 'external-content',
                link: '/editor/element/ext-content/:entityId',
                viewLink: '/ext-content/:entityId',
            },
        };
        const data = aliasData[recentItem.object_type];
        if (data) {
            const element = {
                label: recentItem.name,
                href: data.link.replace(':entityId', recentItem.item_id),
                viewHref: data?.viewLink ? data.viewLink.replace(':entityId', recentItem.item_id) : undefined,
                icon: { type: 'mi', value: data.icon },
                translate: false,
                searchData: {
                    groupKey: 'recent_content',
                    itemType: 'recent_item',
                    labelSuffix: 'none',
                    keywords: {
                        manuLabel: true,
                    },
                },
            };
            fillSearchItems(element, true);
        }
    };
    useEffect(() => {
        if (!isShowCommandMenuPopup) {
            setSearchFieldValue('');
            updateActiveCommandMenuSubGroup('');
            defaultEmptySearchListRef.current.recent_content.items = [];
            setUpdatedDataTime(Date.now());
        }
    }, [isShowCommandMenuPopup]);

    useEffect(() => {
        updateActionType('');
    }, [activeCommandMenuPopupItem]);

    return (
        <>
            <Dialog
                onClose={() => {
                    updateIsShowCommandMenuPopup(false);
                }}
                open={isShowCommandMenuPopup}
                maxWidth={'popupMd'}
                fullWidth
                disableEscapeKeyDown
                sx={styles.dialog}
            >
                <Stack sx={styles.wrapper}>
                    <Box component={'label'} sx={styles.searchHolder}>
                        <TextField
                            sx={styles.searchField}
                            fullWidth={true}
                            placeholder={t('search_placeholder')}
                            name={'command_menu_search'}
                            value={searchFieldValue}
                            onChange={(e) => {
                                setSearchFieldValue(e.target.value);
                            }}
                            onFocus={() => {
                                isFocusSearchField.current = true;
                            }}
                            onBlur={() => {
                                isFocusSearchField.current = false;
                            }}
                            autoFocus={true}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        {activeCommandMenuSubGroup ? (
                                            <Box
                                                onClick={() => {
                                                    updateActiveCommandMenuSubGroup('');
                                                }}
                                                sx={styles.searchStartAdornmentBackButton}
                                            >
                                                <Box sx={styles.searchStartAdornmentBackButtonIcon}>
                                                    <IconMi icon="chevron-left" />
                                                </Box>
                                                <Box>{t('go_back_button_label')}</Box>
                                            </Box>
                                        ) : (
                                            <Box sx={styles.searchStartAdornment}>
                                                <IconMi icon="search" />
                                            </Box>
                                        )}
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Box sx={styles.searchEndAdornment}>
                                            {isMacOs ? <IconMi icon="command-key" /> : <Box sx={{ mr: 0.5 }}>Ctrl</Box>}
                                            <Box>K</Box>
                                        </Box>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    <Box sx={styles.resultContainer} className={'command-menu-result-container'}>
                        {searchValue.trim() > '' && searchResult.length == 0 && (
                            <Box sx={styles.emptyResult}>
                                {t('empty_results')} “<Box component={'strong'}>{searchValue.trim()}</Box>”
                            </Box>
                        )}
                        {searchResult.length > 0 && (
                            <>
                                {searchResult.map((group: any, groupIndex: number) => {
                                    return (
                                        <Fragment key={groupIndex}>
                                            <CommandMenuResultHeading title={group.label} labelType={group.labelType} />
                                            {group.items.map((item: any, itemIndex: number) => {
                                                const active =
                                                    `${activeCommandMenuPopupItem.groupIndex}-${activeCommandMenuPopupItem.itemIndex}` ==
                                                    `${groupIndex}-${itemIndex}`;
                                                return (
                                                    <CommandMenuResultItem
                                                        key={`${groupIndex}-${itemIndex}`}
                                                        item={item}
                                                        isActive={active}
                                                        onMouseOver={() => {
                                                            updateActiveCommandMenuPopupItem(groupIndex, itemIndex);
                                                        }}
                                                        onClick={() => {
                                                            updateActiveCommandMenuPopupItem(groupIndex, itemIndex);
                                                            makeAction();
                                                        }}
                                                        updateActionType={updateActionType}
                                                        currentActionType={actionType}
                                                        index={`${groupIndex}-${itemIndex}`}
                                                    />
                                                );
                                            })}
                                        </Fragment>
                                    );
                                })}
                            </>
                        )}
                    </Box>
                </Stack>
            </Dialog>
            {newModalData && (
                <FormComponent
                    isPopup
                    popupType={'new'}
                    pk={newModalData.url}
                    uid={newModalData.uid}
                    settingsUrl={newModalData.url}
                    onHide={() => {
                        setNewModalData('');
                    }}
                    popupSettings={{}}
                    afterSave={(response: any) => {
                        if (newModalData.redirectAfter) {
                            if (response.data && response.data.redirectUrl) {
                                setTimeout(() => navigate(response.data.redirectUrl), 100);
                            }
                        }
                    }}
                />
            )}
        </>
    );
}
